import React, { useState, useRef, useEffect } from 'react';
import { useNotifications, NotificationItem } from '../../context/NotificationContext';
import { Bell, CheckCheck, Trash2, X, ExternalLink, ShieldAlert, Sparkles, BookOpen, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CATEGORY_TABS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'editorial', label: 'Editorial' },
  { id: 'live_test', label: 'Live Tests' },
  { id: 'support', label: 'Support' },
  { id: 'system', label: 'System' },
];

function formatTimeAgo(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffSec < 60) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function getIconForCategory(cat: string) {
  switch (cat) {
    case 'editorial': return <Sparkles size={15} style={{ color: 'var(--color-primary, #38bdf8)' }} />;
    case 'live_test': return <BookOpen size={15} style={{ color: '#10b981' }} />;
    case 'support': return <MessageSquare size={15} style={{ color: '#f59e0b' }} />;
    case 'security': return <ShieldAlert size={15} style={{ color: '#ef4444' }} />;
    default: return <Bell size={15} style={{ color: 'var(--color-primary, #8b5cf6)' }} />;
  }
}

export function NotificationBellDrawer() {
  const { notifications, unreadCount, activeCategory, setActiveCategory, markAsRead, markAllAsRead, clearAll, loading } = useNotifications();
  const [open, setOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleItemClick = (item: NotificationItem) => {
    if (!item.read) {
      markAsRead(item.id);
    }
    if (item.actionUrl) {
      setOpen(false);
      if (item.actionUrl.startsWith('http')) {
        window.open(item.actionUrl, '_blank');
      } else {
        navigate(item.actionUrl);
      }
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={cardRef}>
      {/* Bell Trigger Button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="ghost-icon-button bell-button"
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        title="Notifications"
        aria-label="Open notifications"
      >
        <Bell size={19} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              background: '#ef4444',
              color: '#ffffff',
              fontSize: '0.65rem',
              fontWeight: 700,
              borderRadius: '999px',
              minWidth: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              boxShadow: '0 0 0 2px var(--color-surface, #ffffff)',
              lineHeight: 1,
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Floating Pop-up Card */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 12px)',
            right: 0,
            width: 'min(400px, calc(100vw - 32px))',
            maxHeight: 'min(560px, calc(100vh - 120px))',
            background: 'var(--color-surface, #ffffff)',
            color: 'var(--color-text, #0f172a)',
            border: '1px solid var(--color-border, rgba(0, 0, 0, 0.1))',
            borderRadius: '20px',
            boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.25), 0 0 1px 1px rgba(0, 0, 0, 0.05)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9999,
            overflow: 'hidden',
            animation: 'fadeInScale 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Card Header */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--color-border, rgba(0, 0, 0, 0.08))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--color-surface, #ffffff)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bell size={18} style={{ color: 'var(--color-primary, #0284c7)' }} />
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--color-text)' }}>
                Notifications
              </h3>
              {unreadCount > 0 && (
                <span
                  style={{
                    fontSize: '0.7rem',
                    background: 'color-mix(in srgb, var(--color-primary, #0284c7) 15%, var(--color-surface-soft))',
                    color: 'var(--color-primary, #0284c7)',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontWeight: 700,
                  }}
                >
                  {unreadCount} new
                </span>
              )}
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-muted, #64748b)',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Category Filter Pills */}
          <div
            style={{
              display: 'flex',
              gap: '6px',
              padding: '10px 16px',
              overflowX: 'auto',
              borderBottom: '1px solid var(--color-border, rgba(0, 0, 0, 0.06))',
              background: 'var(--color-surface-soft, rgba(0, 0, 0, 0.02))',
              scrollbarWidth: 'none',
            }}
          >
            {CATEGORY_TABS.map((tab) => {
              const isActive = activeCategory === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveCategory(tab.id)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '999px',
                    fontSize: '0.75rem',
                    fontWeight: isActive ? 700 : 500,
                    border: '1px solid',
                    borderColor: isActive ? 'var(--color-primary, #0284c7)' : 'var(--color-border, rgba(0, 0, 0, 0.1))',
                    background: isActive ? 'color-mix(in srgb, var(--color-primary, #0284c7) 12%, var(--color-surface-soft))' : 'var(--color-surface, #ffffff)',
                    color: isActive ? 'var(--color-primary, #0284c7)' : 'var(--color-muted, #64748b)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Quick Actions Toolbar */}
          <div
            style={{
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.75rem',
              borderBottom: '1px solid var(--color-border, rgba(0, 0, 0, 0.05))',
              background: 'var(--color-surface, #ffffff)',
            }}
          >
            <button
              onClick={markAllAsRead}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-primary, #0284c7)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: 0,
                fontWeight: 600,
              }}
            >
              <CheckCheck size={14} /> Mark all read
            </button>
            <button
              onClick={clearAll}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-muted, #64748b)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: 0,
                fontWeight: 500,
              }}
            >
              <Trash2 size={14} /> Clear all
            </button>
          </div>

          {/* Notifications Scroll List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {loading && notifications.length === 0 && (
              <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--color-muted, #64748b)', fontSize: '0.85rem' }}>
                Loading notifications…
              </div>
            )}

            {!loading && notifications.length === 0 && (
              <div style={{ padding: '36px 16px', textAlign: 'center', color: 'var(--color-muted, #64748b)' }}>
                <Bell size={28} style={{ opacity: 0.3, marginBottom: '10px' }} />
                <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: 600, color: 'var(--color-text)' }}>No notifications yet</p>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem', opacity: 0.7 }}>You are all caught up!</p>
              </div>
            )}

            {notifications.map((item) => {
              const isUnread = !item.read;
              return (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  style={{
                    padding: '12px 14px',
                    borderRadius: '14px',
                    background: isUnread
                      ? 'color-mix(in srgb, var(--color-primary, #0284c7) 8%, var(--color-surface-soft))'
                      : 'var(--color-surface-soft, rgba(0, 0, 0, 0.03))',
                    border: '1px solid',
                    borderColor: isUnread
                      ? 'color-mix(in srgb, var(--color-primary, #0284c7) 25%, transparent)'
                      : 'var(--color-border, rgba(0, 0, 0, 0.06))',
                    cursor: item.actionUrl ? 'pointer' : 'default',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    gap: '10px',
                    position: 'relative',
                  }}
                >
                  <div style={{ marginTop: '2px', flexShrink: 0 }}>{getIconForCategory(item.category)}</div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <h4
                        style={{
                          margin: 0,
                          fontSize: '0.84rem',
                          fontWeight: isUnread ? 700 : 500,
                          color: 'var(--color-text, #0f172a)',
                          lineHeight: 1.3,
                        }}
                      >
                        {item.title}
                      </h4>
                      <span style={{ fontSize: '0.68rem', color: 'var(--color-muted, #64748b)', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {formatTimeAgo(item.createdAt)}
                      </span>
                    </div>

                    <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem', color: 'var(--color-muted, #64748b)', lineHeight: 1.4 }}>
                      {item.body}
                    </p>

                    {item.actionUrl && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginTop: '6px',
                          fontSize: '0.72rem',
                          color: 'var(--color-primary, #0284c7)',
                          fontWeight: 600,
                        }}
                      >
                        Open <ExternalLink size={10} />
                      </span>
                    )}
                  </div>

                  {isUnread && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '8px',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: 'var(--color-primary, #0284c7)',
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
